import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ErrorHandler, Injector, OnDestroy, OnInit, ViewEncapsulation, inject, signal } from '@angular/core'
import { form } from '@angular/forms/signals'
import { MatSnackBar } from '@angular/material/snack-bar'
import { DateService } from '@lemon/core/service/date.service'
import { AsiakasService } from 'app/_angular/service/asiakas/asiakas.service'
import { FirebaseLemonator } from 'app/_angular/service/firebase-lemonator.service'
import { KirjanpitajaService } from 'app/_angular/service/kirjanpitaja/kirjanpitaja.service'
import { arvonMuutokset } from 'app/_jaettu-angular/_lomakkeet/arvon-muutokset'
import { LadataanService } from 'app/_jaettu-angular/service/ladataan.service'
import { Asiakas, Tilikausi } from 'app/_jaettu-lemonator/model/asiakas'
import {
  ARKISTO_KATEGORIANIMET,
  ArkistoDownloadFileRequest,
  ArkistoDownloadFileResponse,
  ArkistoFile,
  ArkistoFileType,
  ArkistoGetDownloadLinkRequest,
  ArkistoGetDownloadLinkResponse,
  ArkistoKategoria,
  ArkistoService,
} from 'app/_jaettu-lemonator/service/arkisto.service'
import { ArkistoFileTiedostoTyyppiNimet, EI_TILIKAUTTA_AVAIN, getTiedostoIkoni, TiedostoIkoni } from 'app/kirjanpito/hallinto/kirjanpito-hallinto.component'
import { BehaviorSubject, combineLatest, firstValueFrom, Observable, of, Subject } from 'rxjs'
import { debounceTime, distinctUntilChanged, map, startWith, switchMap, takeUntil } from 'rxjs/operators'

interface ArkistoFileDisplay extends ArkistoFile {
  lataajaName: string
  canPreview: boolean
  previewOpen: boolean
  previewLoading: boolean
  previewFileDataUrl: Promise<string>
  previewType: PreviewType
  tiedostoTyyppiNimi: string | null
  tiedostoIkoni: TiedostoIkoni
  kategoria: ArkistoKategoria
}

type PreviewType = 'pdf' | 'jpg' | 'png'

interface TilikausiVaihtoehto {
  avain: string
  label: string
  tooltip: string
  tiedostojenMaara: number
}

interface KategoriaSuodatin {
  avain: ArkistoKategoria | 'kaikki'
  nimi: string
}

interface TilikausiRyhma {
  tilikausiAvain: string
  otsikko: string
  tiedostot: ArkistoFileDisplay[]
}

interface NaytettavatTiedot {
  vaihtoehdot: TilikausiVaihtoehto[]
  ryhmat: TilikausiRyhma[]
}

@Component({
  selector: '[app-tilintarkastus-hallinto]',
  templateUrl: './tilintarkastus-hallinto.component.html',
  styleUrls: ['./tilintarkastus-hallinto.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class TilintarkastusHallintoComponent implements OnInit, OnDestroy {
  private _ngUnsubscribe = new Subject<void>()
  valittuTilikausiSubject = new BehaviorSubject<string | null>(null)
  valittuKategoriaSubject = new BehaviorSubject<ArkistoKategoria | 'kaikki'>('kaikki')
  hakuteksti = form(signal(''))
  private readonly _injector = inject(Injector)

  asiakasObservable: Observable<Asiakas>
  tilikaudetObservable: Observable<Tilikausi[]>
  naytettavatTiedotObservable: Observable<NaytettavatTiedot>

  readonly kategoriaSuodattimet: KategoriaSuodatin[] = [
    { avain: 'kaikki', nimi: 'Kaikki tyypit' },
    ...Object.entries(ARKISTO_KATEGORIANIMET).map(([avain, nimi]) => ({ avain: avain as ArkistoKategoria, nimi })),
  ]

  constructor(
    private _asiakasService: AsiakasService,
    private _firebase: FirebaseLemonator,
    private _arkistoService: ArkistoService,
    private _kirjanpitajaService: KirjanpitajaService,
    private _dateService: DateService,
    private _snackbar: MatSnackBar,
    private _ladataanService: LadataanService,
    private _errorHandler: ErrorHandler,
    private _changeDetectorRef: ChangeDetectorRef,
  ) {}

  ngOnInit() {
    this.asiakasObservable = this._asiakasService.nykyinenAsiakasObservable.pipe(
      takeUntil(this._ngUnsubscribe),
      distinctUntilChanged((a, b) => a?.avain === b?.avain),
    )

    this.tilikaudetObservable = this._asiakasService.nykyisenAsiakkaanTilikaudetObservable.pipe(takeUntil(this._ngUnsubscribe))

    this.naytettavatTiedotObservable = combineLatest([
      this.asiakasObservable,
      this._kirjanpitajaService.kirjanpitajienNimitiedotMapObservable,
      this.valittuTilikausiSubject.asObservable(),
      this.tilikaudetObservable,
      this.valittuKategoriaSubject.asObservable(),
      arvonMuutokset(this.hakuteksti, { injector: this._injector }).pipe(debounceTime(500), startWith('')),
    ]).pipe(
      switchMap(([asiakas, kirjanpitajienNimitiedotMap, valittuTilikausiAvain, tilikaudet, valittuKategoria, hakuteksti]) => {
        if (!asiakas) {
          return of<NaytettavatTiedot>({ vaihtoehdot: [], ryhmat: [] })
        }

        const query = this._firebase
          .firestoreCollection<ArkistoFile>(this._arkistoService.getArkistoMetadataCollection(asiakas.avain))
          .where('poistettu', '==', false)
          .where('type', '==', ArkistoFileType.VISIBLE_FOR_CUSTOMER)

        return query.listen().pipe(map(files => this._rakennaNaytettavatTiedot(files ?? [], kirjanpitajienNimitiedotMap, tilikaudet, valittuTilikausiAvain, valittuKategoria, hakuteksti)))
      }),
    )
  }

  private _rakennaNaytettavatTiedot(
    files: ArkistoFile[],
    kirjanpitajienNimitiedotMap: Map<string, { etunimi: string; sukunimi: string }>,
    tilikaudet: Tilikausi[],
    valittuTilikausiAvain: string | null,
    valittuKategoria: ArkistoKategoria | 'kaikki',
    hakuteksti: string,
  ): NaytettavatTiedot {
    const jarjestetytTilikaudet = [...(tilikaudet ?? [])].sort((a, b) => this._dateService.localDateToNumber(a.alkaa) - this._dateService.localDateToNumber(b.alkaa))
    const tilikausiAvaimet = new Set(jarjestetytTilikaudet.map(tk => tk.avain))
    const hakutekstiLower = (hakuteksti ?? '').toLowerCase()

    const tyyppiJaHakuSuodatetut = files.filter(file => {
      if (valittuKategoria !== 'kaikki' && this._arkistoService.annaKategoria(file) !== valittuKategoria) {
        return false
      }
      if (hakutekstiLower && !file.fileName.toLowerCase().includes(hakutekstiLower) && !file.lisatiedot?.toLowerCase().includes(hakutekstiLower)) {
        return false
      }
      return true
    })

    /**
     * Tiedostot ilman tilikautta käsitellään virtuaalisen EI_TILIKAUTTA_AVAIN-avaimen alla,
     * jotta ne saadaan omaan ryhmään.
     *
     * Huom! Jos tiedosto kuuluu poistetulle tilikaudelle, se tulee näkyviin EI_TILIKAUTTA_AVAIN-avaimen alle
     */
    const annaTiedostonTilikaudet = (file: ArkistoFile): string[] => {
      const aktiiviset = (file.tilikaudet || []).filter(avain => tilikausiAvaimet.has(avain))
      return aktiiviset.length > 0 ? aktiiviset : [EI_TILIKAUTTA_AVAIN]
    }

    const tiedostojenMaaraPerTilikausi = new Map<string, number>()
    for (const file of tyyppiJaHakuSuodatetut) {
      for (const avain of annaTiedostonTilikaudet(file)) {
        tiedostojenMaaraPerTilikausi.set(avain, (tiedostojenMaaraPerTilikausi.get(avain) ?? 0) + 1)
      }
    }

    const vaihtoehdot: TilikausiVaihtoehto[] = []

    const ilmanTilikauttaMaara = tiedostojenMaaraPerTilikausi.get(EI_TILIKAUTTA_AVAIN) ?? 0
    if (ilmanTilikauttaMaara > 0 || valittuTilikausiAvain === EI_TILIKAUTTA_AVAIN) {
      vaihtoehdot.push({
        avain: EI_TILIKAUTTA_AVAIN,
        label: 'Ei valittu',
        tooltip: 'Tiedostot, jotka eivät kuulu millekään tilikaudelle',
        tiedostojenMaara: ilmanTilikauttaMaara,
      })
    }

    for (const tk of jarjestetytTilikaudet) {
      const tiedostojenMaara = tiedostojenMaaraPerTilikausi.get(tk.avain) ?? 0
      if (tiedostojenMaara > 0 || tk.avain === valittuTilikausiAvain) {
        vaihtoehdot.push({
          avain: tk.avain,
          label: '' + tk.loppuu.year,
          tooltip: this._formatTilikausiNimi(tk),
          tiedostojenMaara,
        })
      }
    }

    const naytettavatRawFiles = valittuTilikausiAvain ? tyyppiJaHakuSuodatetut.filter(file => annaTiedostonTilikaudet(file).includes(valittuTilikausiAvain)) : tyyppiJaHakuSuodatetut

    const displayFiles: ArkistoFileDisplay[] = naytettavatRawFiles.map(file => {
      const kirjanpitaja = kirjanpitajienNimitiedotMap.get(file.luoja)
      const previewType = this._getPreviewType(file)
      const kategoria = this._arkistoService.annaKategoria(file)
      return {
        ...file,
        lataajaName: kirjanpitaja ? `${kirjanpitaja.etunimi} ${kirjanpitaja.sukunimi}` : '',
        canPreview: !!previewType,
        previewOpen: false,
        previewLoading: false,
        previewFileDataUrl: null,
        previewType,
        tiedostoTyyppiNimi: file.tiedostoTyyppi ? ArkistoFileTiedostoTyyppiNimet[file.tiedostoTyyppi] : null,
        tiedostoIkoni: getTiedostoIkoni(file),
        kategoria,
      }
    })

    const kieliJarjestys = (kieli?: string) => (kieli === 'fi' ? 0 : 1)
    displayFiles.sort((a, b) => {
      if (!a.tiedostoTyyppiNimi && !b.tiedostoTyyppiNimi) {
        return a.fileName.localeCompare(b.fileName)
      }
      if (!a.tiedostoTyyppiNimi) {
        return -1
      }
      if (!b.tiedostoTyyppiNimi) {
        return 1
      }
      const aKategoria = ARKISTO_KATEGORIANIMET[a.kategoria]
      const bKategoria = ARKISTO_KATEGORIANIMET[b.kategoria]
      return (
        aKategoria.localeCompare(bKategoria) || a.tiedostoTyyppiNimi.localeCompare(b.tiedostoTyyppiNimi) || kieliJarjestys(a.kieli) - kieliJarjestys(b.kieli) || a.fileName.localeCompare(b.fileName)
      )
    })

    const ryhmaMap = new Map<string, ArkistoFileDisplay[]>()
    for (const file of displayFiles) {
      const tiedostonTilikaudet = valittuTilikausiAvain ? [valittuTilikausiAvain] : annaTiedostonTilikaudet(file)
      for (const avain of tiedostonTilikaudet) {
        if (!ryhmaMap.has(avain)) {
          ryhmaMap.set(avain, [])
        }
        // Shallow clone, jotta esikatselun tila on jokaiselle tiedostolle oma
        ryhmaMap.get(avain).push(tiedostonTilikaudet.length > 1 ? { ...file } : file)
      }
    }

    const ryhmat: TilikausiRyhma[] = []

    const ilmanTilikautta = ryhmaMap.get(EI_TILIKAUTTA_AVAIN)
    if (ilmanTilikautta?.length) {
      ryhmat.push({
        tilikausiAvain: EI_TILIKAUTTA_AVAIN,
        otsikko: 'Ei tilikautta',
        tiedostot: ilmanTilikautta,
      })
    }

    for (const tilikausi of jarjestetytTilikaudet) {
      const tiedostot = ryhmaMap.get(tilikausi.avain)
      if (tiedostot?.length) {
        ryhmat.push({
          tilikausiAvain: tilikausi.avain,
          otsikko: `Tilikausi ${this._formatTilikausiNimi(tilikausi)}`,
          tiedostot,
        })
      }
    }

    return { vaihtoehdot, ryhmat }
  }

  private _formatTilikausiNimi(tilikausi: Tilikausi): string {
    const alku = this._dateService.muotoilePaikallinenPaiva(tilikausi.alkaa, 'fi')
    const loppu = this._dateService.muotoilePaikallinenPaiva(tilikausi.loppuu, 'fi')
    return `${alku} – ${loppu}`
  }

  vaihdaTilikausiSuodatin(avain: string) {
    const nykyinen = this.valittuTilikausiSubject.value
    this.valittuTilikausiSubject.next(nykyinen === avain ? null : avain)
  }

  vaihdaKategoriaSuodatin(avain: ArkistoKategoria | 'kaikki') {
    this.valittuKategoriaSubject.next(avain)
  }

  private _getPreviewType(file: ArkistoFile): PreviewType {
    const f = file.fileName.toLowerCase()
    if (f.endsWith('.pdf')) {
      return 'pdf'
    } else if (f.endsWith('.jpg') || f.endsWith('.jpeg')) {
      return 'jpg'
    } else if (f.endsWith('.png')) {
      return 'png'
    }
    return null
  }

  async stopFilePreview(file: ArkistoFileDisplay) {
    file.previewOpen = false
    file.previewLoading = false
    this._changeDetectorRef.markForCheck()
  }

  async previewFile(file: ArkistoFileDisplay) {
    file.previewOpen = true
    file.previewLoading = true
    file.previewType = this._getPreviewType(file)
    this._changeDetectorRef.markForCheck()

    file.previewFileDataUrl = this._downloadFileAsBase64EncodedString(file).then(str => {
      file.previewLoading = false
      this._changeDetectorRef.markForCheck()
      if (file.previewType === 'pdf') {
        return 'data:application/pdf;base64,' + str
      } else if (file.previewType === 'png') {
        return 'data:image/png;base64,' + str
      } else if (file.previewType === 'jpg') {
        return 'data:image/jpeg;base64,' + str
      }
      throw new Error('Unknown previewType: ' + file.previewType)
    })
  }

  private async _downloadFileAsBase64EncodedString(file: ArkistoFile): Promise<string> {
    const asiakas = await firstValueFrom(this.asiakasObservable)
    const req: ArkistoDownloadFileRequest = {
      asiakasAvain: asiakas.avain,
      metadata: file,
    }
    const response = await this._firebase.functionsCall<ArkistoDownloadFileRequest, ArkistoDownloadFileResponse>('arkistoDownloadUploadedFile', req)
    if (!response || response.e) {
      throw new Error('Failed to fetch arkisto file ' + file.avain)
    }
    return response.base64File
  }

  async downloadFile(file: ArkistoFile) {
    this._ladataanService.aloitaLataaminen()
    try {
      const asiakas = await firstValueFrom(this.asiakasObservable)
      const req: ArkistoGetDownloadLinkRequest = {
        asiakasAvain: asiakas.avain,
        metadata: file,
      }
      const resp = await this._firebase.functionsCall<ArkistoGetDownloadLinkRequest, ArkistoGetDownloadLinkResponse>('arkistoGetDownloadLink', req)
      if (!resp || resp.e) {
        throw new Error('Failed to get download link! ' + (resp?.e || 'No response'))
      }
      if (resp.url) {
        // Laitetaan suoraan window.location.href, jotta tiedosto lähtee latautumaan myös Safarilla.
        // Latauslinkissä on content-disposition: attachment, joten tiedosto pitäisi aina latautua, ei aueta uudessa ikkunassa.
        window.location.href = resp.url
      }
    } catch (err) {
      this._errorHandler.handleError(err)
      this._snackbar.open('Tapahtui tuntematon virhe. Ole hyvä ja yritä uudelleen.', 'OK', { duration: 10000 })
    } finally {
      this._ladataanService.lopetaLataaminen()
    }
  }

  ngOnDestroy() {
    this._ngUnsubscribe.next()
    this._ngUnsubscribe.complete()
  }
}
